import { GridIcon } from "../../components/ui/Icons";
import { CodeBlock, Note } from "./shared";

export function SuperbotCardsDocs() {
  return (
    <>
      {/* What is a Superbot Card? */}
      <section id="cards-overview" className="mb-16 scroll-mt-28">
        <h2 className="flex items-center gap-3 font-display text-2xl font-bold text-primary">
          <GridIcon size={24} className="text-amber" />
          What is a Superbot Card?
        </h2>
        <p className="mt-4 text-lg text-secondary">
          A <strong>superbot card</strong> is a dashboard widget that gives your skill a UI. It
          displays data your skill produces, lets users take actions (approve, reject, edit),
          configure settings, and schedule automated runs.
        </p>
        <div className="mt-6 rounded-xl border border-elevated bg-surface/30 p-6">
          <p className="text-secondary">
            Any Claude Code plugin skill can become "card-enabled" by adding a single{" "}
            <code className="rounded bg-surface px-1.5 py-0.5 font-mono text-sm text-amber">
              superbot.json
            </code>{" "}
            file alongside its{" "}
            <code className="rounded bg-surface px-1.5 py-0.5 font-mono text-sm text-amber">
              SKILL.md
            </code>
            . The dashboard reads this manifest and renders the card automatically — no custom UI
            code needed.
          </p>
        </div>
        <h3 className="mt-8 text-lg font-semibold text-secondary">What a card gives you</h3>
        <ul className="mt-4 list-disc space-y-2 pl-6 text-secondary">
          <li>
            <strong>Data display</strong> — Items from your skill rendered as cards with title,
            body, subtitle, and metadata
          </li>
          <li>
            <strong>Action buttons</strong> — Approve, reject, edit, or custom actions on each item
          </li>
          <li>
            <strong>Settings form</strong> — Auto-generated from a schema, so users can configure
            your skill without editing files
          </li>
          <li>
            <strong>Scheduled runs</strong> — Declare when your skill should run and let users
            enable/disable and customize the schedule
          </li>
          <li>
            <strong>Status tracking</strong> — Items flow through a defined lifecycle (e.g., pending
            → approved → rejected)
          </li>
        </ul>
      </section>

      {/* The superbot.json File */}
      <section id="cards-superbot-json" className="mb-16 scroll-mt-28">
        <h2 className="font-display text-2xl font-bold text-primary">The superbot.json File</h2>
        <p className="mt-4 text-lg text-secondary">
          Drop a{" "}
          <code className="rounded bg-surface px-1.5 py-0.5 font-mono text-sm text-amber">
            superbot.json
          </code>{" "}
          file into your skill directory alongside{" "}
          <code className="rounded bg-surface px-1.5 py-0.5 font-mono text-sm text-amber">
            SKILL.md
          </code>
          . It declares everything: card schema, display mapping, actions, settings, schedule, and
          agent type.
        </p>
        <h3 className="mt-8 text-lg font-semibold text-secondary">Directory structure</h3>
        <div className="mt-4">
          <CodeBlock
            language="text"
            code={`skills/
  my-skill/
    SKILL.md          # Skill prompt (required to enable the skill)
    superbot.json     # Manifest — card, settings, schedule, agent
    data.jsonl        # Card items (one JSON object per line)`}
          />
        </div>
        <h3 className="mt-8 text-lg font-semibold text-secondary">Top-level structure</h3>
        <p className="mt-2 text-secondary">
          The manifest has four optional sections under the required identity fields:
        </p>
        <div className="mt-4">
          <CodeBlock
            language="jsonc"
            code={`{
  // Identity (required)
  "name": "My Skill Name",
  "description": "What this skill does",
  "version": "0.1.0",

  // Dashboard card (required for card-enabled skills)
  "card": { ... },

  // Settings form (optional — auto-generates UI)
  "settings": { ... },

  // Schedule (optional — declares when to run)
  "schedule": { ... },

  // Agent (optional — which agent type to use)
  "agent": { ... }
}`}
          />
        </div>
      </section>

      {/* Quick Start */}
      <section id="cards-quick-start" className="mb-16 scroll-mt-28">
        <h2 className="font-display text-2xl font-bold text-primary">Quick Start</h2>
        <p className="mt-4 text-lg text-secondary">
          The simplest possible card-enabled skill. This creates a card that displays items with a
          title and body, and a single "Approve" action button.
        </p>
        <div className="mt-6">
          <CodeBlock
            language="json"
            code={`{
  "name": "My Skill",
  "description": "A simple approval card",

  "card": {
    "dataSource": "data.jsonl",
    "itemSchema": {
      "title": { "type": "string", "label": "Title" },
      "body": { "type": "string", "label": "Content" }
    },
    "display": {
      "title": "title",
      "body": "body"
    },
    "actions": [
      {
        "id": "approve",
        "label": "Approve",
        "icon": "check",
        "style": "primary",
        "handler": "set-status",
        "params": { "status": "approved" }
      }
    ]
  }
}`}
          />
        </div>
        <p className="mt-4 text-secondary">
          That's it. The dashboard will discover this file, render a card section, and show items
          from <code className="rounded bg-surface px-1 py-0.5 font-mono text-xs">data.jsonl</code>{" "}
          with an Approve button on each one.
        </p>
      </section>

      {/* Card Section Reference */}
      <section id="cards-card-section" className="mb-16 scroll-mt-28">
        <h2 className="font-display text-2xl font-bold text-primary">Card Section Reference</h2>
        <p className="mt-4 text-lg text-secondary">
          The{" "}
          <code className="rounded bg-surface px-1.5 py-0.5 font-mono text-sm text-amber">
            card
          </code>{" "}
          section defines how items are stored, displayed, and acted upon.
        </p>

        {/* dataSource */}
        <h3 className="mt-8 text-lg font-semibold text-secondary">
          <code className="text-amber">card.dataSource</code>{" "}
          <span className="text-sm text-muted">(required)</span>
        </h3>
        <p className="mt-2 text-secondary">
          Path to the JSONL data file, relative to the skill directory. Usually{" "}
          <code className="rounded bg-surface px-1 py-0.5 font-mono text-xs">"data.jsonl"</code>.
        </p>

        {/* renderer */}
        <h3 className="mt-8 text-lg font-semibold text-secondary">
          <code className="text-amber">card.renderer</code>{" "}
          <span className="text-sm text-muted">(optional)</span>
        </h3>
        <p className="mt-2 text-secondary">
          Name of the built-in renderer. Defaults to{" "}
          <code className="rounded bg-surface px-1 py-0.5 font-mono text-xs">"default"</code>.
        </p>
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-elevated">
                <th className="py-3 pr-4 text-left font-mono text-muted">Renderer</th>
                <th className="py-3 text-left font-mono text-muted">Description</th>
              </tr>
            </thead>
            <tbody className="text-secondary">
              <tr className="border-b border-surface">
                <td className="py-3 pr-4 font-mono text-amber">"default"</td>
                <td className="py-3">
                  Generic card renderer. Renders items with display mapping and action buttons.
                  Suitable for approval workflows and simple data cards.
                </td>
              </tr>
              <tr className="border-b border-surface">
                <td className="py-3 pr-4 font-mono text-amber">"goal-tracker"</td>
                <td className="py-3">
                  Goal-specific renderer with status state machine
                  (active/paused/completed/abandoned), add-goal form, and show/hide completed
                  toggle.
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* itemSchema */}
        <h3 className="mt-8 text-lg font-semibold text-secondary">
          <code className="text-amber">card.itemSchema</code>{" "}
          <span className="text-sm text-muted">(required)</span>
        </h3>
        <p className="mt-2 text-secondary">
          Defines the fields that each item in the data file can have. Keys are field names, values
          are field definitions.
        </p>
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-elevated">
                <th className="py-3 pr-4 text-left font-mono text-muted">Field</th>
                <th className="py-3 pr-4 text-left font-mono text-muted">Type</th>
                <th className="py-3 text-left font-mono text-muted">Description</th>
              </tr>
            </thead>
            <tbody className="text-secondary">
              <tr className="border-b border-surface">
                <td className="py-3 pr-4 font-mono text-amber">type</td>
                <td className="py-3 pr-4">string</td>
                <td className="py-3">
                  <code className="text-xs">"string"</code>,{" "}
                  <code className="text-xs">"number"</code>, or{" "}
                  <code className="text-xs">"boolean"</code>
                </td>
              </tr>
              <tr className="border-b border-surface">
                <td className="py-3 pr-4 font-mono text-amber">label</td>
                <td className="py-3 pr-4">string</td>
                <td className="py-3">Human-readable label for the field</td>
              </tr>
              <tr className="border-b border-surface">
                <td className="py-3 pr-4 font-mono text-amber">multiline</td>
                <td className="py-3 pr-4">boolean</td>
                <td className="py-3">If true, string fields render as textarea (optional)</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div className="mt-4">
          <CodeBlock
            language="json"
            code={`"itemSchema": {
  "platform": { "type": "string", "label": "Platform" },
  "draft": { "type": "string", "label": "Draft text", "multiline": true },
  "priority": { "type": "number", "label": "Priority" }
}`}
          />
        </div>

        {/* display */}
        <h3 className="mt-8 text-lg font-semibold text-secondary">
          <code className="text-amber">card.display</code>{" "}
          <span className="text-sm text-muted">(required)</span>
        </h3>
        <p className="mt-2 text-secondary">
          Maps display slots to item field names. Determines how items are rendered.
        </p>
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-elevated">
                <th className="py-3 pr-4 text-left font-mono text-muted">Slot</th>
                <th className="py-3 pr-4 text-left font-mono text-muted">Required</th>
                <th className="py-3 text-left font-mono text-muted">Description</th>
              </tr>
            </thead>
            <tbody className="text-secondary">
              <tr className="border-b border-surface">
                <td className="py-3 pr-4 font-mono text-amber">title</td>
                <td className="py-3 pr-4">Yes</td>
                <td className="py-3">Field name for the item header</td>
              </tr>
              <tr className="border-b border-surface">
                <td className="py-3 pr-4 font-mono text-amber">body</td>
                <td className="py-3 pr-4">Yes</td>
                <td className="py-3">Field name for the main content area</td>
              </tr>
              <tr className="border-b border-surface">
                <td className="py-3 pr-4 font-mono text-amber">subtitle</td>
                <td className="py-3 pr-4">No</td>
                <td className="py-3">Field name for secondary info</td>
              </tr>
              <tr className="border-b border-surface">
                <td className="py-3 pr-4 font-mono text-amber">meta</td>
                <td className="py-3 pr-4">No</td>
                <td className="py-3">Field name for metadata/footer line</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div className="mt-4">
          <CodeBlock
            language="json"
            code={`"display": {
  "title": "platform",
  "body": "draft",
  "subtitle": "target",
  "meta": "excerpt"
}`}
          />
        </div>

        {/* statusFlow */}
        <h3 className="mt-8 text-lg font-semibold text-secondary">
          <code className="text-amber">card.statusFlow</code>{" "}
          <span className="text-sm text-muted">(optional)</span>
        </h3>
        <p className="mt-2 text-secondary">
          Ordered list of valid status values for items. Documents the lifecycle and is used by the
          server for validation. Defaults to{" "}
          <code className="rounded bg-surface px-1 py-0.5 font-mono text-xs">["pending"]</code>.
        </p>
        <div className="mt-4">
          <CodeBlock language="json" code={`"statusFlow": ["pending", "approved", "rejected"]`} />
        </div>

        {/* defaultFilter */}
        <h3 className="mt-8 text-lg font-semibold text-secondary">
          <code className="text-amber">card.defaultFilter</code>{" "}
          <span className="text-sm text-muted">(optional)</span>
        </h3>
        <p className="mt-2 text-secondary">
          Default filter applied when fetching items. Defaults to{" "}
          <code className="rounded bg-surface px-1 py-0.5 font-mono text-xs">
            {`{ "status": "pending" }`}
          </code>
          .
        </p>

        {/* actions */}
        <h3 className="mt-8 text-lg font-semibold text-secondary">
          <code className="text-amber">card.actions</code>{" "}
          <span className="text-sm text-muted">(required)</span>
        </h3>
        <p className="mt-2 text-secondary">Buttons displayed on each item. Each action needs:</p>
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-elevated">
                <th className="py-3 pr-4 text-left font-mono text-muted">Field</th>
                <th className="py-3 pr-4 text-left font-mono text-muted">Required</th>
                <th className="py-3 text-left font-mono text-muted">Description</th>
              </tr>
            </thead>
            <tbody className="text-secondary">
              <tr className="border-b border-surface">
                <td className="py-3 pr-4 font-mono text-amber">id</td>
                <td className="py-3 pr-4">Yes</td>
                <td className="py-3">Unique identifier</td>
              </tr>
              <tr className="border-b border-surface">
                <td className="py-3 pr-4 font-mono text-amber">label</td>
                <td className="py-3 pr-4">Yes</td>
                <td className="py-3">Button text</td>
              </tr>
              <tr className="border-b border-surface">
                <td className="py-3 pr-4 font-mono text-amber">icon</td>
                <td className="py-3 pr-4">No</td>
                <td className="py-3">
                  Lucide icon name: <code className="text-xs">"check"</code>,{" "}
                  <code className="text-xs">"x"</code>, <code className="text-xs">"pen-line"</code>,{" "}
                  <code className="text-xs">"pause"</code>, <code className="text-xs">"play"</code>,{" "}
                  <code className="text-xs">"trash-2"</code>,{" "}
                  <code className="text-xs">"plus"</code>
                </td>
              </tr>
              <tr className="border-b border-surface">
                <td className="py-3 pr-4 font-mono text-amber">style</td>
                <td className="py-3 pr-4">Yes</td>
                <td className="py-3">
                  <code className="text-xs">"primary"</code> (green),{" "}
                  <code className="text-xs">"danger"</code> (red), or{" "}
                  <code className="text-xs">"secondary"</code> (neutral)
                </td>
              </tr>
              <tr className="border-b border-surface">
                <td className="py-3 pr-4 font-mono text-amber">handler</td>
                <td className="py-3 pr-4">Yes</td>
                <td className="py-3">Action handler type (see below)</td>
              </tr>
              <tr className="border-b border-surface">
                <td className="py-3 pr-4 font-mono text-amber">params</td>
                <td className="py-3 pr-4">No</td>
                <td className="py-3">Parameters passed to the handler</td>
              </tr>
              <tr className="border-b border-surface">
                <td className="py-3 pr-4 font-mono text-amber">showWhen</td>
                <td className="py-3 pr-4">No</td>
                <td className="py-3">
                  Condition for visibility, e.g.{" "}
                  <code className="text-xs">{`{ "status": "paused" }`}</code>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <h4 className="mt-6 font-semibold text-secondary">Action handlers</h4>
        <div className="mt-4 space-y-4">
          <div className="rounded-lg border border-surface bg-base p-4">
            <p className="font-semibold text-primary">
              <code className="text-amber">"set-status"</code>
            </p>
            <p className="mt-1 text-sm text-muted">Sets the item's status field to a new value.</p>
            <div className="mt-2">
              <CodeBlock
                language="json"
                code={`{ "handler": "set-status", "params": { "status": "approved" } }`}
              />
            </div>
          </div>
          <div className="rounded-lg border border-surface bg-base p-4">
            <p className="font-semibold text-primary">
              <code className="text-amber">"edit-field"</code>
            </p>
            <p className="mt-1 text-sm text-muted">Opens an inline editor for a specific field.</p>
            <div className="mt-2">
              <CodeBlock
                language="json"
                code={`{ "handler": "edit-field", "params": { "field": "draft" } }`}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Settings Section Reference */}
      <section id="cards-settings" className="mb-16 scroll-mt-28">
        <h2 className="font-display text-2xl font-bold text-primary">Settings Section</h2>
        <p className="mt-4 text-lg text-secondary">
          Declares configurable settings for your skill. The dashboard auto-generates a form from
          this schema. Settings values are stored externally at{" "}
          <code className="rounded bg-surface px-1 py-0.5 font-mono text-xs">
            ~/.superbot2/skill-settings/&lt;skillId&gt;/settings.json
          </code>{" "}
          — they survive skill uninstall/reinstall.
        </p>

        <h3 className="mt-8 text-lg font-semibold text-secondary">Supported field types</h3>
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-elevated">
                <th className="py-3 pr-4 text-left font-mono text-muted">Type</th>
                <th className="py-3 pr-4 text-left font-mono text-muted">Renders As</th>
                <th className="py-3 text-left font-mono text-muted">Extra Options</th>
              </tr>
            </thead>
            <tbody className="text-secondary">
              <tr className="border-b border-surface">
                <td className="py-3 pr-4 font-mono text-amber">"string"</td>
                <td className="py-3 pr-4">Text input</td>
                <td className="py-3">
                  <code className="text-xs">multiline: true</code> for textarea,{" "}
                  <code className="text-xs">enum: [...]</code> for dropdown
                </td>
              </tr>
              <tr className="border-b border-surface">
                <td className="py-3 pr-4 font-mono text-amber">"number"</td>
                <td className="py-3 pr-4">Number input</td>
                <td className="py-3">
                  <code className="text-xs">min</code>, <code className="text-xs">max</code>
                </td>
              </tr>
              <tr className="border-b border-surface">
                <td className="py-3 pr-4 font-mono text-amber">"boolean"</td>
                <td className="py-3 pr-4">Toggle switch</td>
                <td className="py-3">—</td>
              </tr>
              <tr className="border-b border-surface">
                <td className="py-3 pr-4 font-mono text-amber">"array"</td>
                <td className="py-3 pr-4">Multi-value list</td>
                <td className="py-3">
                  <code className="text-xs">{`items: { "type": "string" }`}</code>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <h3 className="mt-8 text-lg font-semibold text-secondary">Field definition properties</h3>
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-elevated">
                <th className="py-3 pr-4 text-left font-mono text-muted">Property</th>
                <th className="py-3 pr-4 text-left font-mono text-muted">Type</th>
                <th className="py-3 text-left font-mono text-muted">Description</th>
              </tr>
            </thead>
            <tbody className="text-secondary">
              <tr className="border-b border-surface">
                <td className="py-3 pr-4 font-mono text-amber">type</td>
                <td className="py-3 pr-4">string</td>
                <td className="py-3">Required. Field type.</td>
              </tr>
              <tr className="border-b border-surface">
                <td className="py-3 pr-4 font-mono text-amber">label</td>
                <td className="py-3 pr-4">string</td>
                <td className="py-3">Required. Display label in the form.</td>
              </tr>
              <tr className="border-b border-surface">
                <td className="py-3 pr-4 font-mono text-amber">description</td>
                <td className="py-3 pr-4">string</td>
                <td className="py-3">Help text displayed below the input.</td>
              </tr>
              <tr className="border-b border-surface">
                <td className="py-3 pr-4 font-mono text-amber">default</td>
                <td className="py-3 pr-4">any</td>
                <td className="py-3">Default value when no saved value exists.</td>
              </tr>
              <tr className="border-b border-surface">
                <td className="py-3 pr-4 font-mono text-amber">required</td>
                <td className="py-3 pr-4">boolean</td>
                <td className="py-3">Whether the field must have a value.</td>
              </tr>
              <tr className="border-b border-surface">
                <td className="py-3 pr-4 font-mono text-amber">enum</td>
                <td className="py-3 pr-4">string[]</td>
                <td className="py-3">For strings — renders as dropdown select.</td>
              </tr>
              <tr className="border-b border-surface">
                <td className="py-3 pr-4 font-mono text-amber">multiline</td>
                <td className="py-3 pr-4">boolean</td>
                <td className="py-3">For strings — renders as textarea.</td>
              </tr>
              <tr className="border-b border-surface">
                <td className="py-3 pr-4 font-mono text-amber">min / max</td>
                <td className="py-3 pr-4">number</td>
                <td className="py-3">For numbers — value constraints.</td>
              </tr>
              <tr className="border-b border-surface">
                <td className="py-3 pr-4 font-mono text-amber">items</td>
                <td className="py-3 pr-4">object</td>
                <td className="py-3">
                  For arrays — element type definition, e.g.{" "}
                  <code className="text-xs">{`{ "type": "string" }`}</code>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <h3 className="mt-8 text-lg font-semibold text-secondary">Example</h3>
        <div className="mt-4">
          <CodeBlock
            language="json"
            code={`"settings": {
  "schema": {
    "accounts": {
      "type": "array",
      "items": { "type": "string" },
      "label": "Accounts to monitor",
      "description": "X/Twitter handles to track"
    },
    "checkFrequency": {
      "type": "string",
      "enum": ["hourly", "daily", "weekly"],
      "label": "Check frequency",
      "default": "daily"
    },
    "maxDrafts": {
      "type": "number",
      "label": "Max drafts per session",
      "default": 10,
      "min": 1,
      "max": 50
    },
    "autoApprove": {
      "type": "boolean",
      "label": "Auto-approve drafts",
      "description": "Skip manual review (not recommended)",
      "default": false
    },
    "voiceRules": {
      "type": "string",
      "label": "Brand voice guidelines",
      "multiline": true
    }
  }
}`}
          />
        </div>

        <h3 className="mt-8 text-lg font-semibold text-secondary">Reading settings at runtime</h3>
        <p className="mt-2 text-secondary">From a worker or agent:</p>
        <div className="mt-2">
          <CodeBlock
            language="bash"
            code={`cat ~/.superbot2/skill-settings/<skillId>/settings.json`}
          />
        </div>
        <p className="mt-4 text-secondary">Via the API:</p>
        <div className="mt-2">
          <CodeBlock
            language="text"
            code={`GET /api/skills/<skillId>/settings
→ { "schema": { ... }, "values": { "accounts": [...], "checkFrequency": "daily" } }`}
          />
        </div>
      </section>

      {/* Schedule Section */}
      <section id="cards-schedule" className="mb-16 scroll-mt-28">
        <h2 className="font-display text-2xl font-bold text-primary">Schedule Section</h2>
        <p className="mt-4 text-lg text-secondary">
          Declares when your skill should run. The dashboard shows an enable/disable toggle. Users
          can override the time and days after enabling.
        </p>
        <div className="mt-6">
          <CodeBlock
            language="json"
            code={`"schedule": {
  "default": {
    "time": "09:00",
    "days": ["mon", "tue", "wed", "thu", "fri"]
  },
  "configurable": true
}`}
          />
        </div>
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-elevated">
                <th className="py-3 pr-4 text-left font-mono text-muted">Field</th>
                <th className="py-3 pr-4 text-left font-mono text-muted">Type</th>
                <th className="py-3 text-left font-mono text-muted">Description</th>
              </tr>
            </thead>
            <tbody className="text-secondary">
              <tr className="border-b border-surface">
                <td className="py-3 pr-4 font-mono text-amber">time</td>
                <td className="py-3 pr-4">string</td>
                <td className="py-3">
                  24-hour format <code className="text-xs">"HH:MM"</code>
                </td>
              </tr>
              <tr className="border-b border-surface">
                <td className="py-3 pr-4 font-mono text-amber">times</td>
                <td className="py-3 pr-4">string[]</td>
                <td className="py-3">
                  Multiple times per day (overrides <code className="text-xs">time</code>)
                </td>
              </tr>
              <tr className="border-b border-surface">
                <td className="py-3 pr-4 font-mono text-amber">days</td>
                <td className="py-3 pr-4">string[]</td>
                <td className="py-3">
                  Days of the week: <code className="text-xs">"mon"</code>,{" "}
                  <code className="text-xs">"tue"</code>, etc.
                </td>
              </tr>
              <tr className="border-b border-surface">
                <td className="py-3 pr-4 font-mono text-amber">configurable</td>
                <td className="py-3 pr-4">boolean</td>
                <td className="py-3">
                  Whether users can customize time/days. Default:{" "}
                  <code className="text-xs">true</code>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <p className="mt-4 text-secondary">For multiple runs per day:</p>
        <div className="mt-2">
          <CodeBlock
            language="json"
            code={`"default": {
  "times": ["09:00", "15:00"],
  "days": ["mon", "wed", "fri"]
}`}
          />
        </div>
        <h3 className="mt-8 text-lg font-semibold text-secondary">Schedule lifecycle</h3>
        <div className="mt-4 space-y-3">
          <div className="flex gap-4">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-amber/20 font-mono text-sm text-amber">
              1
            </div>
            <p className="text-secondary">Skill declares schedule in superbot.json</p>
          </div>
          <div className="flex gap-4">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-amber/20 font-mono text-sm text-amber">
              2
            </div>
            <p className="text-secondary">Dashboard shows the schedule with an "Enable" toggle</p>
          </div>
          <div className="flex gap-4">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-amber/20 font-mono text-sm text-amber">
              3
            </div>
            <p className="text-secondary">User enables — schedule added to config</p>
          </div>
          <div className="flex gap-4">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-amber/20 font-mono text-sm text-amber">
              4
            </div>
            <p className="text-secondary">User can override time/days after enabling</p>
          </div>
          <div className="flex gap-4">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-amber/20 font-mono text-sm text-amber">
              5
            </div>
            <p className="text-secondary">User disables — schedule removed</p>
          </div>
        </div>
      </section>

      {/* Agent Section */}
      <section id="cards-agent" className="mb-16 scroll-mt-28">
        <h2 className="font-display text-2xl font-bold text-primary">Agent Section</h2>
        <p className="mt-4 text-lg text-secondary">
          Declares which agent type handles this skill's work when invoked as a scheduled task or by
          the orchestrator.
        </p>
        <div className="mt-6">
          <CodeBlock
            language="json"
            code={`"agent": {
  "type": "social-media-poster",
  "model": "sonnet"
}`}
          />
        </div>
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-elevated">
                <th className="py-3 pr-4 text-left font-mono text-muted">Field</th>
                <th className="py-3 text-left font-mono text-muted">Description</th>
              </tr>
            </thead>
            <tbody className="text-secondary">
              <tr className="border-b border-surface">
                <td className="py-3 pr-4 font-mono text-amber">type</td>
                <td className="py-3">
                  Agent definition name (matches{" "}
                  <code className="text-xs">agents/&lt;name&gt;.md</code>)
                </td>
              </tr>
              <tr className="border-b border-surface">
                <td className="py-3 pr-4 font-mono text-amber">model</td>
                <td className="py-3">
                  Preferred model: <code className="text-xs">"opus"</code>,{" "}
                  <code className="text-xs">"sonnet"</code>,{" "}
                  <code className="text-xs">"haiku"</code>, or{" "}
                  <code className="text-xs">"inherit"</code> (default)
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      {/* Data Flow */}
      <section id="cards-data-flow" className="mb-16 scroll-mt-28">
        <h2 className="font-display text-2xl font-bold text-primary">How Data Flows</h2>
        <p className="mt-4 text-lg text-secondary">
          Understanding the data lifecycle from skill execution to user interaction.
        </p>

        <div className="mt-8 space-y-4">
          <div className="flex gap-4">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-amber/20 font-mono text-sm text-amber">
              1
            </div>
            <div>
              <p className="font-medium text-secondary">Skill writes data</p>
              <p className="text-sm text-muted">
                Your skill (or its agent) appends JSON objects to{" "}
                <code className="rounded bg-surface px-1 py-0.5 font-mono text-xs">data.jsonl</code>{" "}
                — one object per line.
              </p>
            </div>
          </div>
          <div className="flex gap-4">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-amber/20 font-mono text-sm text-amber">
              2
            </div>
            <div>
              <p className="font-medium text-secondary">Dashboard reads and renders</p>
              <p className="text-sm text-muted">
                The API reads the JSONL file, filters by{" "}
                <code className="rounded bg-surface px-1 py-0.5 font-mono text-xs">
                  defaultFilter
                </code>
                , and serves items to the dashboard which renders them using{" "}
                <code className="rounded bg-surface px-1 py-0.5 font-mono text-xs">display</code>{" "}
                mapping.
              </p>
            </div>
          </div>
          <div className="flex gap-4">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-amber/20 font-mono text-sm text-amber">
              3
            </div>
            <div>
              <p className="font-medium text-secondary">User takes action</p>
              <p className="text-sm text-muted">
                User clicks an action button (Approve, Reject, Edit). The handler updates the item
                in the JSONL file.
              </p>
            </div>
          </div>
          <div className="flex gap-4">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-amber/20 font-mono text-sm text-amber">
              4
            </div>
            <div>
              <p className="font-medium text-secondary">Status updated</p>
              <p className="text-sm text-muted">
                The item's status changes (e.g., "pending" → "approved"). The item moves out of the
                default filter view. Downstream agents can read the status to act on approved items.
              </p>
            </div>
          </div>
        </div>

        <h3 className="mt-8 text-lg font-semibold text-secondary">Data item conventions</h3>
        <p className="mt-2 text-secondary">
          Every item in the JSONL file should have these fields:
        </p>
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-elevated">
                <th className="py-3 pr-4 text-left font-mono text-muted">Field</th>
                <th className="py-3 pr-4 text-left font-mono text-muted">Type</th>
                <th className="py-3 text-left font-mono text-muted">Description</th>
              </tr>
            </thead>
            <tbody className="text-secondary">
              <tr className="border-b border-surface">
                <td className="py-3 pr-4 font-mono text-amber">id</td>
                <td className="py-3 pr-4">string</td>
                <td className="py-3">Unique identifier (required)</td>
              </tr>
              <tr className="border-b border-surface">
                <td className="py-3 pr-4 font-mono text-amber">status</td>
                <td className="py-3 pr-4">string</td>
                <td className="py-3">Current lifecycle status (required)</td>
              </tr>
              <tr className="border-b border-surface">
                <td className="py-3 pr-4 font-mono text-amber">createdAt</td>
                <td className="py-3 pr-4">string</td>
                <td className="py-3">ISO 8601 timestamp</td>
              </tr>
              <tr className="border-b border-surface">
                <td className="py-3 pr-4 font-mono text-amber">updatedAt</td>
                <td className="py-3 pr-4">string</td>
                <td className="py-3">ISO 8601 timestamp</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p className="mt-4 text-secondary">
          All other fields come from your{" "}
          <code className="rounded bg-surface px-1 py-0.5 font-mono text-xs">itemSchema</code>.
        </p>
        <div className="mt-4">
          <Note>
            Writing to data.jsonl is typically done by appending a line via shell script or agent.
            Example:{" "}
            <code className="font-mono text-xs">
              echo '{`{"id":"abc","status":"pending","title":"My item","body":"Content"}`}' &gt;&gt;
              data.jsonl
            </code>
          </Note>
        </div>
      </section>

      {/* Full Example */}
      <section id="cards-full-example" className="mb-16 scroll-mt-28">
        <h2 className="font-display text-2xl font-bold text-primary">Full Example</h2>
        <p className="mt-4 text-lg text-secondary">
          A real-world superbot.json for a social media approval workflow. This uses all four
          sections: card, settings, schedule, and agent.
        </p>
        <div className="mt-6">
          <CodeBlock
            language="json"
            code={`{
  "name": "Social Media Approvals",
  "description": "Review and approve social media drafts before posting",
  "version": "0.1.0",

  "card": {
    "dataSource": "data.jsonl",
    "renderer": "default",
    "itemSchema": {
      "platform": { "type": "string", "label": "Platform" },
      "draft": { "type": "string", "label": "Draft text", "multiline": true },
      "target": { "type": "string", "label": "Target" },
      "postUrl": { "type": "string", "label": "Post URL" },
      "excerpt": { "type": "string", "label": "Excerpt" },
      "context": { "type": "string", "label": "Context" }
    },
    "display": {
      "title": "platform",
      "body": "draft",
      "subtitle": "target",
      "meta": "excerpt"
    },
    "statusFlow": ["pending", "approved", "rejected"],
    "defaultFilter": { "status": "pending" },
    "actions": [
      {
        "id": "approve",
        "label": "Approve",
        "icon": "check",
        "style": "primary",
        "handler": "set-status",
        "params": { "status": "approved" }
      },
      {
        "id": "reject",
        "label": "Reject",
        "icon": "x",
        "style": "danger",
        "handler": "set-status",
        "params": { "status": "rejected" }
      },
      {
        "id": "rewrite",
        "label": "Rewrite",
        "icon": "pen-line",
        "style": "secondary",
        "handler": "edit-field",
        "params": { "field": "draft" }
      }
    ]
  },

  "settings": {
    "schema": {
      "accounts": {
        "type": "array",
        "items": { "type": "string" },
        "label": "Accounts to monitor",
        "description": "X/Twitter handles to track for engagement"
      },
      "checkFrequency": {
        "type": "string",
        "enum": ["hourly", "daily", "weekly"],
        "label": "Check frequency",
        "default": "daily"
      },
      "voiceRules": {
        "type": "string",
        "label": "Brand voice guidelines",
        "multiline": true
      }
    }
  },

  "schedule": {
    "default": {
      "time": "10:00",
      "days": ["mon", "tue", "wed", "thu", "fri"]
    },
    "configurable": true
  },

  "agent": {
    "type": "social-media-poster",
    "model": "sonnet"
  }
}`}
          />
        </div>
      </section>

      {/* API Routes */}
      <section id="cards-api" className="mb-16 scroll-mt-28">
        <h2 className="font-display text-2xl font-bold text-primary">API Routes</h2>
        <p className="mt-4 text-lg text-secondary">
          The dashboard interacts with cards through these REST endpoints.
        </p>

        <h3 className="mt-8 text-lg font-semibold text-secondary">Card data</h3>
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-elevated">
                <th className="py-3 pr-4 text-left font-mono text-muted">Method</th>
                <th className="py-3 pr-4 text-left font-mono text-muted">Path</th>
                <th className="py-3 text-left font-mono text-muted">Description</th>
              </tr>
            </thead>
            <tbody className="text-secondary">
              <tr className="border-b border-surface">
                <td className="py-3 pr-4 font-mono text-amber">GET</td>
                <td className="py-3 pr-4 font-mono text-xs">/api/cards</td>
                <td className="py-3">List all card definitions</td>
              </tr>
              <tr className="border-b border-surface">
                <td className="py-3 pr-4 font-mono text-amber">GET</td>
                <td className="py-3 pr-4 font-mono text-xs">/api/cards/:skillId/items</td>
                <td className="py-3">Get items (filtered by defaultFilter)</td>
              </tr>
              <tr className="border-b border-surface">
                <td className="py-3 pr-4 font-mono text-amber">POST</td>
                <td className="py-3 pr-4 font-mono text-xs">/api/cards/:skillId/items</td>
                <td className="py-3">Create a new item</td>
              </tr>
              <tr className="border-b border-surface">
                <td className="py-3 pr-4 font-mono text-amber">PATCH</td>
                <td className="py-3 pr-4 font-mono text-xs">/api/cards/:skillId/items/:itemId</td>
                <td className="py-3">Update item fields</td>
              </tr>
              <tr className="border-b border-surface">
                <td className="py-3 pr-4 font-mono text-amber">DELETE</td>
                <td className="py-3 pr-4 font-mono text-xs">/api/cards/:skillId/items/:itemId</td>
                <td className="py-3">Delete item</td>
              </tr>
            </tbody>
          </table>
        </div>

        <h3 className="mt-8 text-lg font-semibold text-secondary">Skill manifest and settings</h3>
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-elevated">
                <th className="py-3 pr-4 text-left font-mono text-muted">Method</th>
                <th className="py-3 pr-4 text-left font-mono text-muted">Path</th>
                <th className="py-3 text-left font-mono text-muted">Description</th>
              </tr>
            </thead>
            <tbody className="text-secondary">
              <tr className="border-b border-surface">
                <td className="py-3 pr-4 font-mono text-amber">GET</td>
                <td className="py-3 pr-4 font-mono text-xs">/api/skills/:skillId/manifest</td>
                <td className="py-3">Full superbot.json manifest</td>
              </tr>
              <tr className="border-b border-surface">
                <td className="py-3 pr-4 font-mono text-amber">GET</td>
                <td className="py-3 pr-4 font-mono text-xs">/api/skills/:skillId/settings</td>
                <td className="py-3">Settings schema + current values</td>
              </tr>
              <tr className="border-b border-surface">
                <td className="py-3 pr-4 font-mono text-amber">PUT</td>
                <td className="py-3 pr-4 font-mono text-xs">/api/skills/:skillId/settings</td>
                <td className="py-3">Save settings values</td>
              </tr>
              <tr className="border-b border-surface">
                <td className="py-3 pr-4 font-mono text-amber">GET</td>
                <td className="py-3 pr-4 font-mono text-xs">/api/skill-schedules</td>
                <td className="py-3">All skill-declared schedules with enabled state</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      {/* Migration */}
      <section id="cards-migration" className="mb-16 scroll-mt-28">
        <h2 className="font-display text-2xl font-bold text-primary">Migrating from CARD.json</h2>
        <p className="mt-4 text-lg text-secondary">
          If you already have a{" "}
          <code className="rounded bg-surface px-1.5 py-0.5 font-mono text-sm text-amber">
            CARD.json
          </code>
          , upgrading to superbot.json is straightforward. The server will continue reading
          CARD.json files as a fallback, so migration is optional but recommended.
        </p>

        <h3 className="mt-8 text-lg font-semibold text-secondary">Before (CARD.json)</h3>
        <div className="mt-4">
          <CodeBlock
            language="json"
            code={`{
  "name": "My Skill",
  "description": "Does things",
  "dataSource": "data.jsonl",
  "itemSchema": { "title": "string", "body": "string" },
  "display": { "title": "title", "body": "body" },
  "actions": [
    { "id": "approve", "label": "Approve", "style": "primary",
      "handler": "set-status", "params": { "status": "approved" } }
  ]
}`}
          />
        </div>

        <h3 className="mt-8 text-lg font-semibold text-secondary">After (superbot.json)</h3>
        <div className="mt-4">
          <CodeBlock
            language="json"
            code={`{
  "name": "My Skill",
  "description": "Does things",

  "card": {
    "dataSource": "data.jsonl",
    "itemSchema": {
      "title": { "type": "string", "label": "Title" },
      "body": { "type": "string", "label": "Content" }
    },
    "display": { "title": "title", "body": "body" },
    "actions": [
      { "id": "approve", "label": "Approve", "style": "primary",
        "handler": "set-status", "params": { "status": "approved" } }
    ]
  }
}`}
          />
        </div>

        <h3 className="mt-8 text-lg font-semibold text-secondary">Migration steps</h3>
        <div className="mt-4 space-y-3">
          <div className="flex gap-4">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-amber/20 font-mono text-sm text-amber">
              1
            </div>
            <p className="text-secondary">
              Create{" "}
              <code className="rounded bg-surface px-1 py-0.5 font-mono text-xs">
                superbot.json
              </code>{" "}
              in the same directory
            </p>
          </div>
          <div className="flex gap-4">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-amber/20 font-mono text-sm text-amber">
              2
            </div>
            <p className="text-secondary">
              Move <code className="rounded bg-surface px-1 py-0.5 font-mono text-xs">name</code>{" "}
              and{" "}
              <code className="rounded bg-surface px-1 py-0.5 font-mono text-xs">description</code>{" "}
              to the top level
            </p>
          </div>
          <div className="flex gap-4">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-amber/20 font-mono text-sm text-amber">
              3
            </div>
            <p className="text-secondary">
              Wrap remaining fields (dataSource, itemSchema, display, actions) under a{" "}
              <code className="rounded bg-surface px-1 py-0.5 font-mono text-xs">"card"</code> key
            </p>
          </div>
          <div className="flex gap-4">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-amber/20 font-mono text-sm text-amber">
              4
            </div>
            <p className="text-secondary">
              Expand itemSchema shorthand (e.g.,{" "}
              <code className="rounded bg-surface px-1 py-0.5 font-mono text-xs">
                "title": "string"
              </code>
              ) to full format (e.g.,{" "}
              <code className="rounded bg-surface px-1 py-0.5 font-mono text-xs">
                {`"title": { "type": "string", "label": "Title" }`}
              </code>
              )
            </p>
          </div>
          <div className="flex gap-4">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-amber/20 font-mono text-sm text-amber">
              5
            </div>
            <p className="text-secondary">
              Add <code className="rounded bg-surface px-1 py-0.5 font-mono text-xs">settings</code>
              , <code className="rounded bg-surface px-1 py-0.5 font-mono text-xs">schedule</code>,
              and <code className="rounded bg-surface px-1 py-0.5 font-mono text-xs">agent</code>{" "}
              sections as needed
            </p>
          </div>
          <div className="flex gap-4">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-amber/20 font-mono text-sm text-amber">
              6
            </div>
            <p className="text-secondary">
              Delete the old CARD.json (optional — the server falls back to it if superbot.json is
              missing)
            </p>
          </div>
        </div>
      </section>
    </>
  );
}
